import React, { useState, useContext } from "react";
import PushPinIcon from "@mui/icons-material/PushPin";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import HowToVoteOutlinedIcon from "@mui/icons-material/HowToVoteOutlined";
import { Menu, MenuItem, IconButton } from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import { votePoll, setPollStatus, setPollPinned, deletePoll } from "../../services/pollsService";
import { puedeAdministrarEspacio } from "../../utils/permisosEspacio";
import "./polls.scss";

// Cuando el token caduca (dura 8 horas), el servidor rechaza la acción con
// un 401. Reintentar no sirve de nada: hay que volver a iniciar sesión. Se
// avisa con el mensaje real y se lleva a la pantalla de acceso.
const avisarError = (err) => {
  window.alert(err.message || 'No se pudo completar la acción');
  if (err.sesionExpirada) {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }
};

const PollCard = ({ poll, onChange }) => {
  const { currentUser } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Administrador, o encargado del espacio de esta encuesta.
  const isAdmin = puedeAdministrarEspacio(currentUser, poll.spaceId);
  const isOwner = String(poll.createdBy) === String(currentUser?.id);
  const canManage = isAdmin || isOwner;
  const isClosed = poll.status === "closed";

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleVote = async (optionId) => {
    if (isClosed || busy) return;
    setBusy(true);
    try {
      const updated = await votePoll(poll.id, optionId);
      onChange(updated);
    } catch (err) {
      avisarError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleToggleStatus = async () => {
    closeMenu();
    setBusy(true);
    try {
      const updated = await setPollStatus(poll.id, isClosed ? "active" : "closed");
      onChange(updated);
    } catch (err) {
      avisarError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleTogglePin = async () => {
    closeMenu();
    setBusy(true);
    try {
      const updated = await setPollPinned(poll.id, !poll.isPinned);
      onChange(updated);
    } catch (err) {
      avisarError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    closeMenu();
    if (!window.confirm("¿Eliminar esta encuesta? Se perderán todos los votos.")) return;
    setBusy(true);
    try {
      await deletePoll(poll.id);
      onChange(null, poll.id);
    } catch (err) {
      avisarError(err);
      setBusy(false);
    }
  };

  const authorName = poll.creatorName || poll.creatorUsername || "Usuario";
  const authorInitial = authorName.trim().charAt(0).toUpperCase();

  return (
    <div className="poll-card" style={{ "--poll-color": poll.color }}>
      <div className="poll-card-header">
        <div className="poll-card-icon">
          <BarChartRoundedIcon fontSize="small" />
        </div>

        <div className="poll-card-heading">
          <div className="poll-card-title">
            {poll.isPinned && <PushPinIcon className="poll-pin-icon" />}
            <h4>{poll.question}</h4>
          </div>
          {(poll.spaceName || isClosed) && (
            <div className="poll-card-tags">
              {poll.spaceName && (
                <span className="poll-space-badge">
                  <PlaceOutlinedIcon fontSize="inherit" />
                  {poll.spaceName}
                </span>
              )}
              {isClosed && <span className="poll-closed-badge">Cerrada</span>}
            </div>
          )}
        </div>

        {canManage && (
          <>
            <IconButton size="small" className="poll-menu-btn" onClick={openMenu} disabled={busy}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {isAdmin && (
                <MenuItem onClick={handleTogglePin}>
                  <PushPinIcon fontSize="small" style={{ marginRight: 8 }} />
                  {poll.isPinned ? "Quitar de fijadas" : "Fijar arriba del feed"}
                </MenuItem>
              )}
              <MenuItem onClick={handleToggleStatus}>
                {isClosed
                  ? <LockOpenIcon fontSize="small" style={{ marginRight: 8 }} />
                  : <LockIcon fontSize="small" style={{ marginRight: 8 }} />}
                {isClosed ? "Reabrir votación" : "Cerrar votación"}
              </MenuItem>
              <MenuItem onClick={handleDelete} className="poll-menu-delete">
                <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
                Eliminar encuesta
              </MenuItem>
            </Menu>
          </>
        )}
      </div>

      {poll.description && <p className="poll-card-description">{poll.description}</p>}

      <div className="poll-options">
        {poll.options.map((opt) => {
          const voted = poll.myVotes?.includes(opt.id);
          return (
            <button
              key={opt.id}
              className={`poll-option ${voted ? "voted" : ""} ${isClosed ? "readonly" : ""}`}
              onClick={() => handleVote(opt.id)}
              disabled={busy || isClosed}
            >
              <span className="poll-option-fill" style={{ width: `${opt.percent}%` }} />
              <span className="poll-option-radio" aria-hidden="true" />
              <span className="poll-option-label">{opt.text}</span>
              <span className="poll-option-stats">
                <strong>{opt.percent}%</strong>
                <span className="poll-option-count">{opt.voteCount}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="poll-card-footer">
        <span className="poll-footer-meta">
          <HowToVoteOutlinedIcon fontSize="inherit" />
          {poll.totalVotes} voto{poll.totalVotes === 1 ? "" : "s"} · {poll.allowMultiple ? "Respuesta múltiple" : "Respuesta única"}
        </span>
        <span className="poll-author">
          <span className="poll-author-avatar">{authorInitial}</span>
          {authorName}
        </span>
      </div>
    </div>
  );
};

export default PollCard;
